package com.steamstreet.vegasful.browser.account.intelligence

import com.steamstreet.vegasful.browser.account.Account
import com.steamstreet.vegasful.browser.account.GraphQL
import com.steamstreet.vegasful.browser.account.loginPanel
import com.steamstreet.vegasful.browser.components.autoGrow
import com.steamstreet.vegasful.browser.components.typewriterPlaceholders
import com.steamstreet.vegasful.css.VegasfulStyles
import com.steamstreet.vegasful.css.css
import com.steamstreet.vegasful.graphql.api.ConversationInput
import com.steamstreet.vegasful.web.ClientController
import com.steamstreet.vegasful.web.analytics.Analytics
import com.steamstreet.vegasful.web.analytics.AnalyticsEvent
import com.steamstreet.vegasful.web.analytics.AnalyticsParam
import io.ktor.client.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import kotlinx.browser.document
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.css.*
import kotlinx.dom.clear
import kotlinx.html.*
import kotlinx.html.dom.append
import kotlinx.html.js.onClickFunction
import org.w3c.dom.Element
import org.w3c.dom.HTMLTextAreaElement

private const val intelligenceInputId: String = "intelligence-input"
private const val statusId: String = "intelligence-status"

/** The `context` every failure from this feature is reported under. */
private const val INTELLIGENCE_CONTEXT: String = "intelligence"


/**
 * A list of predefined input hints or suggestions for the user when interacting with the intelligence system.
 * These hints provide examples of queries users can ask, such as event recommendations or venue suggestions.
 * The list supports dynamic placeholder functionality in the text area input within the intelligence interface.
 */
private val inputHints = listOf(
    "What’s happening tonight?",
    "Any rooftop shows this weekend?",
    "Find me something low-key and fun.",
    "Where’s a good comedy show?",
    "Show me what’s electric in Vegas tonight."
)


/**
 * The `Intelligence` class handles the main interaction and behavior
 * of the Vegasful Intelligence feature. It provides a dynamic UI for
 * logged-in and logged-out users and facilitates backend queries related
 * to user input.
 *
 * @property element The root DOM element where the Intelligence feature will be rendered.
 * @property scope The coroutine scope for managing asynchronous operations related to the class tasks.
 *
 * Implements the `ClientController` interface to define a `run` method for initializing the UI and handling user interactions.
 */
class Intelligence(private val element: Element, private val scope: CoroutineScope) : ClientController {
    private val httpClient = HttpClient()

    override suspend fun run() {
        element.append {
            if (!Account.isLoggedIn()) {
                div {
                    h1 {
                        css {
                            fontSize = (1.75).rem
                            marginBottom = (1).rem
                        }
                        +"Login"
                    }
                    div {
                        css {
                            fontWeight = FontWeight.w300
                            marginBottom = (2).rem
                        }
                        +"To use Vegasful Intelligence, you must have a Vegasful account"
                    }
                    div {
                        css {
                            maxWidth = 484.px
                            margin = Margin(0.px, LinearDimension.auto)
                        }
                        loginPanel()
                    }
                }
            } else {
                div {
                    textArea {
                        this.id = intelligenceInputId
                        placeholder =
                            "Ask anything about what you'd like to do... (e.g. 'Are there any jazz or soul events coming up?')"
                        css {
                            width = 100.pct
                            height = 200.px
                            padding = Padding(10.px)
                            borderRadius = 10.px
                        }
                    }

                    intelligenceButton("Ask Vegasful") {
                        val query = (document.getElementById(intelligenceInputId) as? HTMLTextAreaElement)?.value
                        if (query != null) {
                            scope.launch {
                                executeQuery(query)
                            }
                        }
                    }
                }
            }
        }

        val textInput = document.getElementById(intelligenceInputId) as? HTMLTextAreaElement
        textInput?.autoGrow(2)
        textInput?.typewriterPlaceholders(scope, inputHints)
    }


    private suspend fun executeQuery(query: String) {
        // The question itself never leaves the browser: only how long it was. Intelligence
        // questions are free text about what somebody wants to do on a given night, which is
        // exactly the sort of thing `docs/analytics-plan.md` keeps out of the analytics stream.
        Analytics.track(
            AnalyticsEvent.INTELLIGENCE_QUERIED,
            AnalyticsParam.QUERY_LENGTH to query.length
        )

        element.clear()
        element.append {
            div {
                p {
                    css {
                        fontWeight = FontWeight.w600
                        fontSize = 18.px
                        textAlign = TextAlign.left
                    }
                    +query
                }
                div {
                    id = statusId
                    +"Analyzing Vegas events..."
                }
            }
        }

        console.log("Running query: $query")
        val id = GraphQL.client {
            conversations {
                start(ConversationInput(query)) {
                    id
                }
            }
        }.conversations.start.id

        // Poll for conversation results using simple HTTP polling
        pollForConversation(id)
    }

    private suspend fun pollForConversation(id: String) {
        val statusElement = document.getElementById(statusId)!!

        try {
            // Poll the conversation endpoint until complete
            while (true) {
                delay(1000) // Poll every second

                // Fetch HTML from the server using Ktor
                val response = httpClient.get("/conversations/$id")

                // Check for HTTP errors
                if (!response.status.isSuccess()) {
                    console.error("HTTP error: ${response.status.value} ${response.status.description}")
                    val errorText = response.bodyAsText()
                    console.error("Error response:", errorText)
                    statusElement.innerHTML = "<p style='color: red; padding: 20px; text-align: center;'>An error occurred while processing your request. Please try again.</p>"
                    Analytics.apiError(INTELLIGENCE_CONTEXT, "conversation poll failed", response.status.value)
                    reportResponded(success = false)
                    break
                }

                val html = response.bodyAsText()

                // Update the status div with the server-rendered HTML
                statusElement.innerHTML = html

                // If the HTML doesn't contain "conversation-status", it's complete
                if (!html.contains("conversation-status")) {
                    reportResponded(success = true)
                    break
                }
            }
        } catch (e: Exception) {
            console.error("Error polling conversation:", e)
            statusElement.innerHTML = "<p style='color: red; padding: 20px; text-align: center;'>An error occurred. Please try again.</p>"
            Analytics.apiError(INTELLIGENCE_CONTEXT, "conversation polling threw")
            reportResponded(success = false)
        }
    }

    /**
     * Report that Intelligence finished. Paired one-to-one with `intelligence_queried`, so the
     * gap between the two counts is the share of questions that never got an answer — which is
     * the number worth watching for a feature that polls a model in the background.
     */
    private fun reportResponded(success: Boolean) {
        Analytics.track(
            AnalyticsEvent.INTELLIGENCE_RESPONDED,
            AnalyticsParam.SUCCESS to success
        )
    }

    /**
     * Renders an interactive button with specific styling and behavior tied to a click event.
     *
     * @param name The display name of the button.
     * @param onClick The action to be executed when the button is clicked.
     */
    private fun FlowContent.intelligenceButton(name: String, onClick: () -> Unit) {
        a(href = "#") {
            css {
                width = 100.pct
                display = Display.flex
                this.color = Color.white
                backgroundColor = VegasfulStyles.purple
                fontSize = 18.px
                fontWeight = FontWeight.w600
                padding = Padding(8.px)
                borderRadius = 5.px
                marginTop = 8.px

            }
            onClickFunction = {
                onClick()
            }
            span {
                css {
                    flexGrow = 1.0
                    textAlign = TextAlign.center
                }
                +name
            }
        }
    }
}
